import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";
import { limitSlotElements } from "../../../utils/limitSlotElements";
import { logger } from "../../../utils/console";
import { SortDirection } from "../interfaces/directions";
import { UxSort } from "../interfaces/ux-sort";
import { WidthChangeEvent } from "../interfaces/width-change";

@Component({
    tag: "ux-table-cell-neo",
    styleUrls: ["table-cell-neo.scss"],
    shadow: true
})
export class TableRowNeo {
    @Element() host!: HTMLUxTableCellNeoElement;

    /**
     * If true, the cell will be rendered as a header cell.
     */
    @Prop() readonly head: boolean;

    /**
     * The number of columns this cell should span.
     */
    @Prop() readonly span: number;

    /**
     *  The key to sort by
     */
    @Prop() readonly sortkey: string;

    /**
     * If true, the cell will be sortable.
     */
    @Prop() readonly sortable: boolean;

    /**
     *  The current sort direction
     */
    @Prop() readonly sorted: SortDirection;

    /**
     *
     */
    @Prop() readonly colsize: string;

    /**
     * The index of the cell
     */
    @Prop() readonly index: number;

    /**
     * If true, the cell will be loading.
     */
    @Prop() readonly loading: boolean;

    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                default: ["ux-table-row-neo"]
            }
        });
        this.handleSpanChange();
        this.cellWidth = this.host.clientWidth;
    }

    @State() cellWidth: number;

    /**
     * UXSort Event - Emits when the cell is sorted
     */
    @Event() uxSort: EventEmitter<UxSort>;

    /**
     * Handle span change and emit cell changed event
     */
    @Watch("span")
    handleSpanChange() {
        if (this.span > 0) {
            this.host.style.setProperty("--ux-cell-span", this.span.toString());
        }
    }

    @State() direction: SortDirection = "none";

    private handleSortChange(event: KeyboardEvent | MouseEvent) {
        if (!this.sortkey) {
            logger.warn("No sort key provided for table cell");
            return;
        }
        if (event instanceof KeyboardEvent) {
            if (event.key !== "Enter") {
                return;
            }
        }
        this.direction = this.direction === "descending" ? "ascending" : this.direction === "ascending" ? "none" : "descending";
        this.uxSort.emit({ dir: this.direction, key: this.sortkey });
        this.host.setAttribute("aria-sort", this.direction);
        if (this.direction === "none") {
            this.host.removeAttribute("sorted");
            return;
        }
        this.host.setAttribute("sorted", this.direction);
    }
    @State() width = 100; // Initial width

    /**
     * WidthChange Event - Emits when the width of the cell is changed
     */
    @Event() widthChange: EventEmitter<WidthChangeEvent>;

    render() {
        return (
            <Host role={this.head ? "columnheader" : "cell"} loading={this.loading}>
                <div id="loadingContainer"></div>
                {this.head && !this.loading && (
                    <div
                        id="labelContainer"
                        onClick={(e) => this.handleSortChange(e)}
                        onKeyDown={(e) => this.handleSortChange(e)}
                        tabIndex={0}
                    >
                        <ux-copy variant="tiny" strong>
                            <slot />
                        </ux-copy>
                        {this.sortable && <ux-table-cell-sort-neo direction={this.direction}></ux-table-cell-sort-neo>}
                    </div>
                )}
                {!this.head && !this.loading && <slot />}
            </Host>
        );
    }
}
